import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Icon } from '../design-system/icons';
import { SetupScaffold } from '../components/onboarding/SetupScaffold';
import { UploadInstructionsOverlay } from '../components/onboarding/UploadInstructionsOverlay';
import { ONBOARDING_V2 } from '../components/onboarding/onboardingV2Routes';
import {
  dejarFotoParaGenerar,
  prepararImagen,
  ImagenInvalidaError,
} from '../lib/primeraGeneracion';
import { marcarProductoDelOnboarding } from '../lib/onboardingTracking';
import {
  getExampleProducts,
  getInstructionExamples,
  SETUP_CATEGORIES_KEY,
  SETUP_GENERATION_KEY,
  type ExampleProduct,
} from '../components/onboarding/accountSetup';

/**
 * Cuestionario post-registro, paso 4: producto (Figma node 105:688).
 * Réplica de los estados del frame reutilizando tokens del DS v3 (ADR-717).
 *
 * El usuario elige **1 de 2 opciones** (acordeón single-select, radio):
 * - **Subir una foto propia:** nombre del producto + recuadro para subir foto.
 *   Al tocar el recuadro se abre un overlay de instrucciones; desde ahí se elige
 *   el archivo. La foto subida se puede borrar.
 * - **Usar una imagen de ejemplo:** colapsada muestra miniaturas; expandida
 *   muestra la lista de ejemplos seleccionables (single-select).
 *
 * Seleccionar una opción **colapsa la otra** con animación (grid-rows 0fr↔1fr).
 * Lo cargado/seleccionado se **conserva** al alternar de opción (estado en memoria).
 * "Generar imagen" se habilita cuando la opción activa tiene contenido (foto subida
 * o ejemplo elegido); por ahora no hace nada.
 */

type Option = 'upload' | 'example' | null;

/** Radio del DS (aro + punto): el DS no exporta `solid/circle-select`, se arma con CSS.
 *  El aro sin seleccionar es `#18181B` (oscuro), tal cual Figma node 171:187 (ADR-739);
 *  seleccionado, además se rellena. */
function RadioDot({ selected }: { selected: boolean }) {
  return (
    <span
      aria-hidden
      className={`grid size-5 shrink-0 place-items-center rounded-full border border-[#18181B] transition-colors duration-200 ${
        selected ? 'bg-[#18181B]' : ''
      }`}
    >
      <span
        className={`size-1.5 rounded-full bg-white transition-transform duration-200 ${
          selected ? 'scale-100' : 'scale-0'
        }`}
      />
    </span>
  );
}

/** Contenedor colapsable animado por altura (grid-template-rows 0fr↔1fr). */
function Collapse({ open, children }: { open: boolean; children: ReactNode }) {
  return (
    <div
      className="grid transition-[grid-template-rows] duration-300 ease-out"
      style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
    >
      <div className="overflow-hidden">{children}</div>
    </div>
  );
}

export const AccountSetupProduct = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [option, setOption] = useState<Option>(null);
  const [productName, setProductName] = useState('');
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedExample, setSelectedExample] = useState<string | null>(null);
  const [showInstructions, setShowInstructions] = useState(false);

  // Rubro(s) elegidos en el paso 2. Se leen UNA vez al montar y alimentan las dos cosas
  // que dependen del rubro en esta pantalla: los productos de ejemplo y los ejemplos de
  // la modal de instrucciones.
  const [categorias] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(SETUP_CATEGORIES_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? (parsed as string[]) : [];
    } catch {
      return [];
    }
  });
  // Productos de ejemplo del rubro (los que no tienen set propio caen al default).
  const [products] = useState<ExampleProduct[]>(() => getExampleProducts(categorias));
  // Ejemplos de "cómo subir una buena foto" del rubro (ADR-2026-08-01-instrucciones-de-foto-por-rubro).
  const instructionExamples = getInstructionExamples(categorias);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const objectUrlRef = useRef<string | null>(null);
  /** La foto ya normalizada (convertida y redimensionada) lista para subir. */
  const archivoRef = useRef<File | null>(null);
  // Dos errores distintos: el del archivo (al elegirlo) y el que rebota de la pantalla
  // de generación cuando la generación real falló y devolvió al usuario acá.
  const [errorArchivo, setErrorArchivo] = useState<string | null>(
    () => (location.state as { errorGeneracion?: string } | null)?.errorGeneracion ?? null,
  );

  // Necesita sesión (viene post-login). Si no hay, vuelve a la bienvenida.
  useEffect(() => {
    let active = true;
    supabase.auth.getSession().then(({ data }) => {
      if (active && !data.session) navigate(ONBOARDING_V2.bienvenida, { replace: true });
    });
    return () => {
      active = false;
    };
  }, [navigate]);

  // Liberar el object URL de la imagen subida al desmontar.
  useEffect(() => () => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
  }, []);

  const setUploaded = (url: string | null) => {
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    objectUrlRef.current = url;
    setUploadedImage(url);
  };

  // La foto se valida y normaliza acá (formato, tamaño, HEIC→JPEG, resize) y NO al
  // generar: si el archivo no sirve, el usuario se entera al elegirlo.
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite volver a elegir el mismo archivo
    if (!file) return;
    setErrorArchivo(null);
    try {
      const preparada = await prepararImagen(file);
      archivoRef.current = preparada.file;
      setUploaded(preparada.previewUrl);
      setShowInstructions(false);
    } catch (err) {
      archivoRef.current = null;
      setUploaded(null);
      setErrorArchivo(
        err instanceof ImagenInvalidaError
          ? err.message
          : 'No pudimos procesar esa imagen. Probá con otra.',
      );
    }
  };

  const canGenerate =
    (option === 'upload' && !!uploadedImage) || (option === 'example' && !!selectedExample);

  const handleGenerate = () => {
    if (!canGenerate) return;
    // Qué se genera: el id del ejemplo (resultados pre-armados, sin consumir créditos)
    // o `upload` (generación REAL con la foto del usuario, ver ADR-2026-07-31-onboarding-nuevo-es-el-flujo-real).
    const generacion = option === 'example' ? selectedExample! : 'upload';
    try {
      localStorage.setItem(SETUP_GENERATION_KEY, generacion);
    } catch {}
    // El mismo dato, a la empresa: el `localStorage` no se puede mirar desde afuera y
    // queríamos saber cuántos llegan con producto propio
    // (ADR-2026-08-07-onboarding-producto-y-descarga-en-supabase). Best-effort, sin esperar.
    marcarProductoDelOnboarding(generacion);
    if (option === 'upload') {
      if (!archivoRef.current) {
        // No debería pasar (la preview y el archivo se setean juntos), pero si pasa el
        // usuario tiene que enterarse: un `return` mudo se ve como un botón roto.
        setErrorArchivo('Se perdió la foto. Volvé a subirla e intentá de nuevo.');
        return;
      }
      // El File viaja por memoria: la pantalla de generación arranca desde ahí.
      dejarFotoParaGenerar(archivoRef.current, productName);
    }
    navigate(ONBOARDING_V2.setupGenerando);
  };

  // La card entera es clickeable para seleccionar la opción (no sólo el header);
  // colapsada muestra cursor + hover como afordancia. El botón del header se
  // mantiene para teclado / lector de pantalla (rol radio).
  const cardClass = (active: boolean) =>
    `flex flex-col rounded-[16px] border bg-[#FAFAFA] p-5 transition-[border-color,box-shadow] duration-300 ease-out ${
      active
        ? 'border-[#18181B] shadow-[inset_0_0_0_1px_#18181B]'
        : 'cursor-pointer border-[#D4D4D8] hover:border-[#9F9FA9]'
    }`;

  // Botón "Generar imagen" (footer fijo). El gradiente azul se funde por opacidad
  // sobre el gris (no se puede animar color sólido ↔ gradiente → crossfade).
  const generateButton = (
    <button
      type="button"
      onClick={handleGenerate}
      disabled={!canGenerate}
      className={`relative flex w-full items-center justify-center gap-2 overflow-hidden rounded-[12px] bg-[#9F9FA9] p-4 text-[16px] font-[700] leading-[19px] text-[#FAFAFA] transition-[filter] duration-200 md:w-auto md:px-10 ${
        canGenerate ? 'hover:brightness-110' : 'cursor-not-allowed'
      }`}
    >
      <span
        aria-hidden
        className={`pointer-events-none absolute inset-0 bg-[linear-gradient(181.83deg,#2563F5_8.76%,#0436AE_90.56%)] transition-opacity duration-300 ease-out ${
          canGenerate ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <span className="relative z-10 flex items-center justify-center gap-2">
        <Icon name="solid/sparkles" size={20} />
        Generar imagen
      </span>
    </button>
  );

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <SetupScaffold footer={generateButton}>
        <h1 className="text-ds-heading-xl text-[#18181B] md:text-center">
          Todo listo, subí tu producto y probá Estudio Atlas gratis
        </h1>

        <div className="flex flex-col gap-6" role="radiogroup" aria-label="Elegí cómo cargar tu producto">
          {/* ── Opción 1: subir una foto propia ── */}
          <div className={cardClass(option === 'upload')} onClick={() => setOption('upload')}>
            <button
              type="button"
              role="radio"
              aria-checked={option === 'upload'}
              onClick={() => setOption('upload')}
              className="flex w-full items-start gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 focus-visible:ring-offset-2 rounded-[8px]"
            >
              <span className="flex flex-1 flex-col gap-1.5">
                <span className="text-[16px] font-[600] leading-[21px] text-[#18181B]">
                  Subir una foto propia
                </span>
                <span className="text-[14px] font-[400] leading-[19px] text-[#52525C]">
                  Usá una imagen de tu producto
                </span>
              </span>
              <RadioDot selected={option === 'upload'} />
            </button>

            <Collapse open={option === 'upload'}>
              <div className="flex flex-col gap-6 pt-6">
                {/* Nombre del producto */}
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="product-name" className="text-[14px] font-[600] leading-[19px] text-[#18181B]">
                    Nombre del producto
                  </label>
                  <input
                    id="product-name"
                    type="text"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder="Ej: Remera básica negra"
                    // 16px en mobile: por debajo de eso Safari en iOS hace auto-zoom al
                    // enfocar y deja la pantalla ampliada y corrida. Recién en `md:` va
                    // el 14px del diseño. Ver ADR-2026-08-03-inputs-16px-en-mobile-sin-auto-zoom.
                    className="w-full rounded-[12px] border border-[#D4D4D8] bg-white px-4 py-3 text-[16px] font-[400] leading-[22px] md:text-[14px] md:leading-[19px] text-[#18181B] outline-none placeholder:text-[#71717B] focus:border-[#18181B]"
                  />
                </div>

                {/* Foto del producto */}
                <div className="flex flex-col gap-1.5">
                  <span className="text-[14px] font-[600] leading-[22px] text-[#18181B]">Foto del producto</span>
                  {uploadedImage ? (
                    <div className="relative size-[120px] overflow-hidden rounded-[12px]">
                      <img src={uploadedImage} alt="Foto del producto subida" className="size-full object-cover" />
                      <button
                        type="button"
                        onClick={() => setUploaded(null)}
                        aria-label="Eliminar foto"
                        className="absolute right-1.5 top-1.5 flex items-center rounded-[8px] bg-white p-1 text-[#18181B] shadow-sm transition-colors hover:bg-[#F4F4F5]"
                      >
                        <Icon name="outline/trash" size={12} />
                      </button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setShowInstructions(true)}
                      className="flex w-full flex-col items-center justify-center gap-3 rounded-[8px] border-2 border-dashed border-[#3B82F6] bg-white px-4 py-6 outline-none transition-colors hover:bg-[#3B82F6]/5 focus-visible:ring-2 focus-visible:ring-[#3B82F6]/40"
                    >
                      <Icon name="outline/image-plus" size={24} className="text-[#3B82F6]" />
                      <span className="text-[14px] font-[600] leading-[18px] text-[#3B82F6]">Subir foto</span>
                    </button>
                  )}

                  {/* Archivo rechazado (formato bloqueado, >100 MB, HEIC que no se
                      pudo convertir): se avisa al elegirlo, no al generar. */}
                  {errorArchivo && (
                    <p
                      role="alert"
                      className="mt-3 rounded-[8px] bg-[#FEE2E2] px-3 py-2 text-[13px] font-[500] leading-[18px] text-[#991B1B]"
                    >
                      {errorArchivo}
                    </p>
                  )}
                </div>
              </div>
            </Collapse>
          </div>

          {/* ── Opción 2: usar una imagen de ejemplo ── */}
          <div className={cardClass(option === 'example')} onClick={() => setOption('example')}>
            <button
              type="button"
              role="radio"
              aria-checked={option === 'example'}
              onClick={() => setOption('example')}
              className="flex w-full items-start gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 focus-visible:ring-offset-2 rounded-[8px]"
            >
              <span className="flex flex-1 flex-col gap-1.5">
                <span className="text-[16px] font-[600] leading-[21px] text-[#18181B]">
                  Usar una imagen de ejemplo
                </span>
                <span className="text-[14px] font-[400] leading-[19px] text-[#52525C]">
                  Te dejamos opciones para que pruebes
                </span>
              </span>
              <RadioDot selected={option === 'example'} />
            </button>

            {/* Colapsada: miniaturas de preview */}
            <Collapse open={option !== 'example'}>
              <div className="flex gap-3 pt-6">
                {products.map((p) => (
                  <div key={p.id} className="flex items-center rounded-[12px] border border-[#E4E4E7] bg-white p-1.5">
                    <img src={p.image} alt="" draggable={false} className="size-11 rounded-[8px] object-cover" />
                  </div>
                ))}
              </div>
            </Collapse>

            {/* Expandida: lista seleccionable */}
            <Collapse open={option === 'example'}>
              <div className="flex flex-col gap-4 pt-6" role="radiogroup" aria-label="Elegí un producto de ejemplo">
                {products.map((p) => {
                  const sel = selectedExample === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      role="radio"
                      aria-checked={sel}
                      onClick={() => setSelectedExample(p.id)}
                      className={`relative flex w-full items-center rounded-[16px] border bg-white p-3 outline-none transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[#1644EB]/40 ${
                        sel ? 'border-[#18181B]' : 'border-[#E4E4E7] hover:border-[#D4D4D8]'
                      }`}
                    >
                      <img src={p.image} alt="" draggable={false} className="size-[60px] shrink-0 rounded-[12px] object-cover" />
                      <span className="flex-1 px-3 text-center text-[14px] font-[600] leading-[18px] text-[#18181B]">
                        {p.name}
                      </span>
                      <span
                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-opacity duration-200 ${
                          sel ? 'opacity-100' : 'opacity-0'
                        }`}
                      >
                        <Icon name="outline/check" size={24} className="text-[#18181B]" />
                      </span>
                    </button>
                  );
                })}
              </div>
            </Collapse>
          </div>
        </div>

      </SetupScaffold>

      {showInstructions && (
        <UploadInstructionsOverlay
          onClose={() => setShowInstructions(false)}
          onPickFile={() => fileInputRef.current?.click()}
          examples={instructionExamples}
        />
      )}
    </>
  );
};

export default AccountSetupProduct;
